// src/services/gamePictureService.js
//
// Pictures for the cards and boxes of a generated game (Layout Builder ▸
// Game generator ▸ Drag and Drop / Memory / Sorting editors).
//
//   makeGamePicture({ topic, word, idea, look, quality })
//       One AI picture from the Image studio's generateImage function
//       (admins only — the function checks). The full-size PNG it saves is
//       shrunk to a small square WebP for the game card.
//   uploadGamePicture({ topic, file, word })
//       A picture from your computer, shrunk the same way.
//   gamePicturePrompt(...)
//       The words sent for one picture (shown in the editor so you can change
//       them before pressing Make).
//
// Where the small pictures go:  topic-images/games/{topic}/{word}-{time}.webp
// (topic-images/ is the public-read, admin-write folder the portal already
// shows topic pictures from). Download links carry their own token, so
// students can always load them.
//
// Writes NOTHING to Firestore. The picture's link only reaches a game when
// the draft is saved / published from the generator, like any other edit.

import { getDownloadURL, ref as storageRef, uploadBytes } from "firebase/storage";
import { httpsCallable } from "firebase/functions";
import { functions, storage } from "../firebase/firebaseConfig";
import { fileKeyOf, legacyKey, exactKey } from "../utils/topicKey";

/** Looks offered for game pictures (the Image studio's `style` keys). */
export const GAME_PIC_LOOKS = [
  { key: "sbl", label: "SBL cartoon" },
  { key: "diagram", label: "Simple diagram" },
  { key: "storybook", label: "Storybook" },
  { key: "realistic", label: "Photo-like" },
];

export const GAME_PIC_QUALITIES = [
  { key: "fast", label: "Fast (cheapest)" },
  { key: "standard", label: "Standard" },
  { key: "best", label: "Best detail (costs most)" },
];

const SUBJECT_WORDS = { math: "math", science: "science", socialstudies: "social studies", other: "" };
const SIZE = 384; // px — sharp on a smart board, small enough to load fast

/** An emoji icon (data:image/svg+xml) rather than a real picture. */
export const isEmojiPic = (src) => String(src || "").startsWith("data:image/svg+xml");
/** A real picture: an uploaded / AI / linked image, not an emoji or nothing. */
export const isRealPic = (src) => Boolean(src) && !isEmojiPic(src);

const clean = (s) => String(s || "").replace(/_{2,}/g, "…").replace(/\s+/g, " ").trim();

/**
 * The words for one picture. `word` is what the card or box says; `idea`
 * (typed in the editor) replaces the automatic description.
 */
export const gamePicturePrompt = ({ topic = {}, word = "", idea = "" }) => {
  const title = clean(topic.title || topic.slug);
  const subject = SUBJECT_WORDS[topic.subject] || "";
  const what = clean(idea) || clean(word);
  return [
    `A picture for a ${subject ? `${subject} ` : ""}matching game for K-8 students about "${title}".`,
    `Show: ${what}.`,
    "It goes on a small game card, so: ONE clear subject, large and centred, filling most of the square, " +
      "on a plain white background. It must still be easy to recognise when shown about 80 pixels wide. " +
      "No words, letters or numbers in the picture unless the description asks for them.",
  ]
    .join("\n\n")
    .slice(0, 1990);
};

const stamp = () => new Date().toISOString().replace(/[-:]/g, "").replace(/\..*$/, "").replace("T", "-");
const topicFolder = (topic) => fileKeyOf(legacyKey(topic?.slug || topic?.id) || exactKey(topic?.slug || topic?.id) || "topic");
const wordFile = (word) =>
  String(word || "picture")
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "")
    .slice(0, 40) || "picture";

/**
 * Square SIZE px WebP (JPEG if the browser can't) of a picture. `fit` =
 * "cover" crops to fill (AI pictures are already square), "contain" keeps the
 * whole picture on a transparent square (uploads). null if it can't be read
 * (e.g. a CORS-blocked link).
 */
const shrink = (src, fit = "cover") =>
  new Promise((resolve) => {
    const img = new Image();
    img.crossOrigin = "anonymous";
    const timer = setTimeout(() => resolve(null), 25000);
    img.onload = () => {
      clearTimeout(timer);
      try {
        const c = document.createElement("canvas");
        c.width = SIZE;
        c.height = SIZE;
        const ctx = c.getContext("2d");
        const iw = img.naturalWidth || SIZE;
        const ih = img.naturalHeight || SIZE;
        const s = fit === "contain" ? Math.min(SIZE / iw, SIZE / ih) : Math.max(SIZE / iw, SIZE / ih);
        ctx.drawImage(img, (SIZE - iw * s) / 2, (SIZE - ih * s) / 2, iw * s, ih * s);
        c.toBlob(
          (blob) => {
            if (blob && blob.type === "image/webp") return resolve(blob);
            c.toBlob((jpg) => resolve(jpg || null), "image/jpeg", 0.88);
          },
          "image/webp",
          0.86
        );
      } catch {
        resolve(null); // tainted canvas
      }
    };
    img.onerror = () => {
      clearTimeout(timer);
      resolve(null);
    };
    img.src = src;
  });

const save = async (blob, topic, word) => {
  const ext = { "image/webp": "webp", "image/png": "png", "image/gif": "gif" }[blob.type] || "jpg";
  const path = `topic-images/games/${topicFolder(topic)}/${wordFile(word)}-${stamp()}.${ext}`;
  const r = storageRef(storage, path);
  await uploadBytes(r, blob, { contentType: blob.type, cacheControl: "public, max-age=31536000" });
  return getDownloadURL(r);
};

const friendly = (err) => {
  const code = String(err?.code || "");
  if (code.includes("permission-denied")) return "Only SBL admin accounts can make pictures.";
  if (code.includes("deadline-exceeded")) return "The picture took too long. Try again.";
  if (code.includes("resource-exhausted")) return "Google's picture limit was reached. Wait a minute, then carry on.";
  if (code.includes("not-found") && /generateImage|not found/i.test(err?.message || "")) {
    return "The picture maker (generateImage) isn't deployed. See the Image studio setup.";
  }
  return err?.message || String(err);
};

/** One AI picture → resolves { url, prompt }. */
export const makeGamePicture = async ({ topic, word = "", idea = "", prompt = "", look = "sbl", quality = "fast" }) => {
  const text = String(prompt || "").trim() || gamePicturePrompt({ topic, word, idea });
  let res;
  try {
    res = await httpsCallable(functions, "generateImage", { timeout: 170000 })({
      prompt: text,
      style: look,
      model: quality,
      aspectRatio: "1:1",
      name: `game-${topicFolder(topic)}-${wordFile(word || idea)}`,
      refs: [],
    });
  } catch (err) {
    throw new Error(friendly(err));
  }
  const big = res?.data?.url;
  if (!big) throw new Error("No picture came back. Try different words.");
  const blob = await shrink(big, "cover");
  // can't shrink (CORS)? the full-size picture still works, just heavier
  const url = blob ? await save(blob, topic, word || idea) : big;
  return { url, prompt: text, small: Boolean(blob) };
};

/** A picture file from the computer → resolves { url }. */
export const uploadGamePicture = async ({ topic, file, word = "" }) => {
  if (!file || !/^image\//.test(file.type || "")) throw new Error("Pick a picture file (PNG, JPG, WebP or GIF).");
  if (file.size > 15 * 1024 * 1024) throw new Error("That picture is over 15 MB. Pick a smaller one.");
  const local = URL.createObjectURL(file);
  try {
    const blob = (await shrink(local, "contain")) || file;
    return { url: await save(blob, topic, word) };
  } finally {
    URL.revokeObjectURL(local);
  }
};

/* ------------------------------------------------------------------ */
/* Label It! boards — one big picture that students label              */
/* ------------------------------------------------------------------ */

export const BOARD_ASPECTS = [
  { key: "4:3", label: "4 : 3 (good for most)" },
  { key: "16:9", label: "16 : 9 (wide — maps)" },
  { key: "1:1", label: "1 : 1 (square)" },
  { key: "3:4", label: "3 : 4 (tall — a plant, a body)" },
];
const BOARD_MAX = 1600; // px on the long side

/** The words for a board picture. `idea` is what to draw. */
export const boardPicturePrompt = ({ topic = {}, idea = "" }) => {
  const title = clean(topic.title || topic.slug);
  const subject = SUBJECT_WORDS[topic.subject] || "";
  const what = clean(idea) || `the main parts of "${title}"`;
  return [
    `A picture for a ${subject ? `${subject} ` : ""}labeling game for K-8 students about "${title}".`,
    `Show: ${what}.`,
    "Students will drag name tiles onto the parts of this picture, so every part they will label must be clearly " +
      "visible, separate and easy to point at, with some space around it. Simple, uncluttered, high contrast. " +
      "ABSOLUTELY NO words, letters, numbers, labels or captions anywhere in the picture — naming the parts is the game.",
  ]
    .join("\n\n")
    .slice(0, 1990);
};

/** A board-sized WebP keeping the picture's own shape (no crop). */
const shrinkBoard = (src) =>
  new Promise((resolve) => {
    const img = new Image();
    img.crossOrigin = "anonymous";
    const timer = setTimeout(() => resolve(null), 30000);
    img.onload = () => {
      clearTimeout(timer);
      try {
        const iw = img.naturalWidth || BOARD_MAX;
        const ih = img.naturalHeight || BOARD_MAX;
        const s = Math.min(1, BOARD_MAX / Math.max(iw, ih));
        const c = document.createElement("canvas");
        c.width = Math.round(iw * s);
        c.height = Math.round(ih * s);
        c.getContext("2d").drawImage(img, 0, 0, c.width, c.height);
        c.toBlob(
          (blob) => {
            if (blob && blob.type === "image/webp") return resolve(blob);
            c.toBlob((jpg) => resolve(jpg || null), "image/jpeg", 0.9);
          },
          "image/webp",
          0.88
        );
      } catch {
        resolve(null);
      }
    };
    img.onerror = () => {
      clearTimeout(timer);
      resolve(null);
    };
    img.src = src;
  });

/** One AI board picture → resolves { url, prompt }. */
export const makeBoardPicture = async ({ topic, idea = "", prompt = "", look = "sbl", quality = "standard", aspect = "4:3" }) => {
  const text = String(prompt || "").trim() || boardPicturePrompt({ topic, idea });
  let res;
  try {
    res = await httpsCallable(functions, "generateImage", { timeout: 170000 })({
      prompt: text,
      style: look,
      model: quality,
      aspectRatio: aspect,
      name: `labelit-${topicFolder(topic)}`,
      refs: [],
    });
  } catch (err) {
    throw new Error(friendly(err));
  }
  const big = res?.data?.url;
  if (!big) throw new Error("No picture came back. Try different words.");
  const blob = await shrinkBoard(big);
  const url = blob ? await save(blob, topic, "board") : big;
  return { url, prompt: text };
};

/** A board picture from the computer → resolves { url }. */
export const uploadBoardPicture = async ({ topic, file }) => {
  if (!file || !/^image\//.test(file.type || "")) throw new Error("Pick a picture file (PNG, JPG, WebP or GIF).");
  if (file.size > 25 * 1024 * 1024) throw new Error("That picture is over 25 MB. Pick a smaller one.");
  const local = URL.createObjectURL(file);
  try {
    const blob = (await shrinkBoard(local)) || file;
    return { url: await save(blob, topic, "board") };
  } finally {
    URL.revokeObjectURL(local);
  }
};
